import { useCallback } from 'react';
import { type RouteProp, useRoute } from '@react-navigation/native';
import { type DeliveryOrderDetail } from '@sg/graphql-schema';

import { type OrderingContext } from '@order/features/ordering';
import { useTelemetry } from '@order/Telemetry';

import { type PartialLineItem } from '../../../../graphql';
import { getCheckoutTelemetryData } from '../../utils';

/**
 * Telemetry events for bag/checkout.
 */
export const useBagTracking = () => {
  const { params } = useRoute<BagScreenRouteParams>();
  const { track } = useTelemetry();

  const trackBagView = useCallback(() => {
    track('bag.view', { entryPoint: params?.referrer });
  }, [params?.referrer, track]);

  const trackBagReady = useCallback(
    (context: OrderingContext<PartialLineItem, DeliveryOrderDetail>) => {
      track('bag.ready', getCheckoutTelemetryData(context));
    },
    [track],
  );

  const trackLocationNamePressed = useCallback(() => {
    track('bag.location-name.pressed');
  }, [track]);

  const trackWantedTimePressed = useCallback(() => {
    track('bag.wanted-time.pressed');
  }, [track]);

  const trackWantedTimeChanged = useCallback(() => {
    track('bag.wanted-time.changed');
  }, [track]);

  const trackDeliveryPreferencesPressed = useCallback(() => {
    track('bag.delivery-preferences.pressed');
  }, [track]);

  const trackDropoffLocationChanged = useCallback(() => {
    track('bag.dropoff-location.changed');
  }, [track]);

  const trackViewMenuPressed = useCallback(() => {
    track('bag.view-menu.pressed');
  }, [track]);

  const trackLineItemPressed = useCallback(() => {
    track('bag.line-item.pressed');
  }, [track]);

  const trackLineItemRemoved = useCallback(() => {
    track('bag.line-item.removed');
  }, [track]);

  const trackLineItemQuantityChanged = useCallback(() => {
    track('bag.line-item.quantity.changed');
  }, [track]);

  const trackLoyaltyInfoPressed = useCallback(() => {
    track('bag.reward-info.pressed');
  }, [track]);

  const trackLoyaltyTermsPressed = useCallback(() => {
    track('bag.reward-terms.pressed');
  }, [track]);

  const trackLoyaltySignedOutPressed = useCallback(() => {
    track('bag.loyalty-signed-out.pressed');
  }, [track]);

  const trackApplyRewardPressed = useCallback(() => {
    track('bag.apply-reward.pressed');
  }, [track]);

  const trackRemoveRewardPressed = useCallback(() => {
    track('bag.remove-reward.pressed');
  }, [track]);

  const trackPromoCodeAddPressed = useCallback(() => {
    track('bag.promo-code.add.pressed');
  }, [track]);

  const trackUtensilsUsagePressed = useCallback(() => {
    track('bag.utensils-usage.pressed');
  }, [track]);

  const trackContinuePressed = useCallback(() => {
    track('bag.continue.pressed');
  }, [track]);

  const trackChangePaymentMethodPressed = useCallback(() => {
    track('bag.change-payment-method.pressed');
  }, [track]);

  const trackCreditPressed = useCallback(() => {
    track('bag.credit.pressed');
  }, [track]);

  const trackPaymentMethodPressed = useCallback(() => {
    track('bag.payment-method.pressed');
  }, [track]);

  const trackRedeemGiftCardPressed = useCallback(() => {
    track('bag.redeem-gift-card.pressed');
  }, [track]);

  const trackRedeemGiftCardSubmitted = useCallback(() => {
    track('bag.redeem-gift-card.submitted');
  }, [track]);

  const trackCheckoutStart = useCallback(
    (context: OrderingContext<PartialLineItem, DeliveryOrderDetail>) => {
      track('checkout.start', getCheckoutTelemetryData(context));
    },
    [track],
  );

  return {
    trackBagView,
    trackBagReady,
    trackLocationNamePressed,
    trackWantedTimePressed,
    trackWantedTimeChanged,
    trackDeliveryPreferencesPressed,
    trackDropoffLocationChanged,
    trackViewMenuPressed,
    trackLineItemPressed,
    trackLineItemRemoved,
    trackLineItemQuantityChanged,
    trackLoyaltyInfoPressed,
    trackLoyaltyTermsPressed,
    trackLoyaltySignedOutPressed,
    trackApplyRewardPressed,
    trackRemoveRewardPressed,
    trackPromoCodeAddPressed,
    trackUtensilsUsagePressed,
    trackContinuePressed,
    trackChangePaymentMethodPressed,
    trackCreditPressed,
    trackPaymentMethodPressed,
    trackRedeemGiftCardPressed,
    trackRedeemGiftCardSubmitted,
    trackCheckoutStart,
  };
};

// ─── Types ──────────────────────────────────────────────────────────────────

type BagScreenRouteParams = RouteProp<BagScreenProps>;
type BagScreenProps = Readonly<{
  params?: { referrer?: string };
}>;
